import { stammdatenService } from '../services/StammdatenService.js';

/**
 * Filter-Seitenpanel (Bereich / Posten / Einsatzort / Qualifikation). Hält den Filterzustand als
 * Modul-State — bleibt über Wochenwechsel erhalten. Die vier Auswahlen werden je Betriebsstätte im
 * localStorage gespeichert und beim ersten Laden bzw. Betriebsstättenwechsel von dort geladen (siehe
 * sicherstellenFilterZustand), der Suchbegriff bewusst nicht. Kennt selbst keine Render-Logik des Dienstplans, sondern ruft bei
 * jeder Änderung nur den übergebenen onChange-Callback auf — vermeidet einen zirkulären Import mit
 * dienstplan.js, das den eigentlichen Re-Render übernimmt.
 *
 * Die Auswahl-Sets sind eine Einschluss-Liste, kein Ausschluss: ein leeres Set bedeutet "kein Filter
 * gesetzt" = alles sichtbar (Default-Zustand, siehe "Alle Bereiche/Posten/Einsatzorte/Qualifikationen"-
 * Zeile ganz oben in jeder Liste). Erst ein Klick auf ein einzelnes Element schaltet auf eine echte
 * Einschluss-Auswahl um. Vorher war es umgekehrt (alles startete ausgewählt, Abwählen eines von vielen
 * Elementen ließ die Filterleiste mit fast allen anderen als Chips zumüllen) — siehe git-history für
 * den alten Stand.
 *
 * Vereinfachung ggü. dem Design-Handoff: Checkbox-Änderungen werden sofort angewendet (kein
 * getrennter "ausgewählt, aber noch nicht angewendet"-Zwischenzustand); der "Anwenden"-Button
 * schließt daher nur das Panel.
 */

let bereichAuswahl = new Set();
let postenAuswahl = new Set();
let einsatzortAuswahl = new Set();
let qualifikationAuswahl = new Set();
// Suchbegriff (normalisiert: getrimmt, klein) — '' = keine Suche aktiv.
let suchbegriff = '';
// Betriebsstätte, deren Filter gerade geladen ist — weicht sie von der aktuellen ab, wird deren Filter geladen.
let filterBetriebsstaette = null;
// Nach dem Laden die Schichtvorlagen einmalig auf den gewählten Bereich umstellen — sobald eine Ansicht mit
// onBereichAuswahl rendert (der Bedarfsplaner hat keine Vorlagen und übergibt keins).
let bereichAbgleichOffen = false;
// Panel-Neuzeichnen + Re-Render der aktuell sichtbaren Ansicht; bei jedem renderFilterPanel aktualisiert,
// damit das (nur einmal verdrahtete) Suchfeld immer die gerade aktive Ansicht neu rendert.
let aktuellesAendern = null;

const SUCHE_DEBOUNCE_MS = 300;
const FILTER_STORAGE_KEY = 'dienstplan.filter';

function resetFilterZustand() {
	bereichAuswahl = new Set();
	postenAuswahl = new Set();
	einsatzortAuswahl = new Set();
	qualifikationAuswahl = new Set();
	suchbegriff = '';
}

function speichereFilterZustand() {
	try {
		localStorage.setItem(`${FILTER_STORAGE_KEY}.${filterBetriebsstaette}`, JSON.stringify({
			Bereiche: [...bereichAuswahl],
			Posten: [...postenAuswahl],
			Einsatzorte: [...einsatzortAuswahl],
			Qualifikationen: [...qualifikationAuswahl],
		}));
	} catch {
		// localStorage nicht verfügbar (z.B. blockiert) — Filter gilt dann nur bis zum Neuladen
	}
}

/**
 * Lädt den gespeicherten Filter der aktuellen Betriebsstätte. IDs, die es in den aktuellen Stammdaten nicht
 * mehr gibt (gelöscht/deaktiviert), werden verworfen — sonst wäre der Filter aktiv, ohne dass etwas passt
 * oder ein Chip dafür erscheint, und der Dienstplan bliebe leer.
 */
function ladeFilterZustand(bereiche, postenListe, einsatzorte, qualifikationen) {
	filterBetriebsstaette = stammdatenService.aktuelleBetriebsstaette;
	let gespeichert = {};
	try {
		gespeichert = JSON.parse(localStorage.getItem(`${FILTER_STORAGE_KEY}.${filterBetriebsstaette}`)) ?? {};
	} catch {
		// nichts gespeichert, ungültig oder localStorage blockiert — ohne Filter starten
	}
	const nurVorhandene = (ids, liste) => new Set((Array.isArray(ids) ? ids : []).filter((id) => liste.some((e) => e._id === id)));
	bereichAuswahl = nurVorhandene(gespeichert.Bereiche, bereiche);
	postenAuswahl = nurVorhandene(gespeichert.Posten, postenListe);
	einsatzortAuswahl = nurVorhandene(gespeichert.Einsatzorte, einsatzorte);
	qualifikationAuswahl = nurVorhandene(gespeichert.Qualifikationen, qualifikationen);
	bereichAbgleichOffen = true;
}

function normalisiere(text) {
	return (text ?? '').toString().trim().toLowerCase();
}

/**
 * Passt eine Mitarbeiterzeile zur Suche? Treffer sind Vorname, Nachname (auch kombiniert, in beiden
 * Reihenfolgen), Personalnummer und die Titel der übergebenen Schichten — im Wochenplan nur die Schichten
 * DIESER Posten-Zeile, in der Mitarbeiteransicht alle Schichten der Person. Ohne Suchbegriff immer true.
 * @param {object} mitarbeiter
 * @param {array<object>} schichten Schichten der Zeile
 */
function istSucheTreffer(mitarbeiter, schichten = []) {
	if (!suchbegriff) {
		return true;
	}
	const vorname = mitarbeiter.Personalien?.Vorname ?? '';
	const nachname = mitarbeiter.Personalien?.Nachname ?? '';
	const texte = [
		`${vorname} ${nachname}`,
		`${nachname} ${vorname}`,
		`${nachname}, ${vorname}`,
		mitarbeiter.Personalien?.Personalnummer,
	];
	return texte.some((t) => normalisiere(t).includes(suchbegriff)) || schichten.some(istSchichtTreffer);
}

function istSucheAktiv() {
	return !!suchbegriff;
}

/** Passt der Titel einer Schicht zur aktiven Suche? (für die Hervorhebung der Schichtkarten) */
function istSchichtTreffer(schicht) {
	return !!suchbegriff && normalisiere(schicht?.Titel).includes(suchbegriff);
}

/**
 * Verdrahtet das Suchfeld oben im Filter-Panel (einmalig): Eingabe wird um SUCHE_DEBOUNCE_MS verzögert
 * angewendet und nur, wenn sich der normalisierte Begriff tatsächlich ändert (jeder Re-Render lädt die
 * Wochenschichten neu). Enter wendet sofort an, Escape bzw. das ✕ leeren die Suche.
 */
function initSuchfeld() {
	const input = document.getElementById('filter-suche');
	const leeren = document.getElementById('filter-suche-x');
	if (!input || input.dataset.verdrahtet) return;
	input.dataset.verdrahtet = 'true';
	let timer = null;
	const anwenden = () => {
		clearTimeout(timer);
		leeren.hidden = input.value === '';
		const neu = normalisiere(input.value);
		if (neu === suchbegriff) return;
		suchbegriff = neu;
		if (aktuellesAendern) aktuellesAendern();
	};
	input.addEventListener('input', () => {
		leeren.hidden = input.value === '';
		clearTimeout(timer);
		timer = setTimeout(anwenden, SUCHE_DEBOUNCE_MS);
	});
	input.addEventListener('keydown', (e) => {
		if (e.key === 'Enter') {
			anwenden();
		} else if (e.key === 'Escape' && input.value !== '') {
			e.stopPropagation();
			input.value = '';
			anwenden();
		}
	});
	leeren.addEventListener('click', () => {
		input.value = '';
		anwenden();
		input.focus();
	});
}

/** Gleicht das Suchfeld mit dem Zustand ab (z.B. nach Zurücksetzen/Chip entfernen), außer während der Eingabe. */
function synchronisiereSuchfeld() {
	const input = document.getElementById('filter-suche');
	if (!input || document.activeElement === input) return;
	if (normalisiere(input.value) !== suchbegriff) input.value = suchbegriff;
	document.getElementById('filter-suche-x').hidden = input.value === '';
}

function sicherstellenFilterZustand(bereiche, postenListe, einsatzorte, qualifikationen) {
	if (filterBetriebsstaette !== stammdatenService.aktuelleBetriebsstaette) {
		ladeFilterZustand(bereiche, postenListe, einsatzorte, qualifikationen);
	}
}

function istBereichSichtbar(bereichID) {
	return bereichAuswahl.size === 0 || bereichAuswahl.has(bereichID);
}

function istPostenSichtbar(postenID) {
	return postenAuswahl.size === 0 || postenAuswahl.has(postenID);
}

/**
 * Eine Mitarbeiterzeile ist sichtbar, wenn mindestens eine ihrer aktiven Qualifikationen im Filter
 * ausgewählt ist (oder gar kein Qualifikations-Filter aktiv ist). Ohne auswertbare Qualifikationen
 * (leeres/undefiniertes Array) wird nicht ausgeblendet, um MA ohne gepflegte Qualifikation nicht
 * versehentlich verschwinden zu lassen.
 * @param {array<string>} qualifikationIDs
 */
function istQualifikationSichtbar(qualifikationIDs) {
	if (qualifikationAuswahl.size === 0) {
		return true;
	}
	if (!qualifikationIDs || qualifikationIDs.length === 0) {
		return true;
	}
	return qualifikationIDs.some((id) => qualifikationAuswahl.has(id));
}

/**
 * Eine Mitarbeiterzeile ist sichtbar, wenn mindestens einer ihrer zugeordneten Einsatzorte im Filter
 * ausgewählt ist, oder gar kein Einsatzort-Filter aktiv ist. Anders als bei istQualifikationSichtbar
 * werden MA ohne zugeordneten Einsatzort ausgeblendet, sobald ein Einsatzort-Filter aktiv ist — ein
 * gewählter Einsatzort-Filter soll gezielt auf die dort eingesetzten MA einschränken, nicht zusätzlich
 * alle undefinierten mitanzeigen.
 * @param {array<string>} einsatzortIDs
 */
function istEinsatzortSichtbar(einsatzortIDs) {
	if (einsatzortAuswahl.size === 0) {
		return true;
	}
	if (!einsatzortIDs || einsatzortIDs.length === 0) {
		return false;
	}
	return einsatzortIDs.some((id) => einsatzortAuswahl.has(id));
}

/**
 * @param {function} [onUmschalten] (id, angehakt) => void — nach dem An-/Abwählen dieser Zeile
 */
function erstelleFilterZeile(container, id, label, auswahlSet, anzahl, onChange, onUmschalten = null) {
	const zeile = document.createElement('div');
	zeile.className = `fopt${auswahlSet.has(id) ? ' on' : ''}`;
	const cnt = anzahl === undefined ? '' : `<span class="cnt">${anzahl}</span>`;
	zeile.innerHTML = `<span class="cb"><i class="bi bi-check"></i></span><span class="fopt-label">${label}</span>${cnt}`;
	zeile.addEventListener('click', () => {
		const angehakt = !auswahlSet.has(id);
		if (angehakt) {
			auswahlSet.add(id);
		} else {
			auswahlSet.delete(id);
		}
		if (onUmschalten) onUmschalten(id, angehakt);
		onChange();
	});
	container.appendChild(zeile);
}

/**
 * "Alle X"-Zeile ganz oben in jeder Filterliste — angehakt, solange für diese Kategorie kein Filter
 * gesetzt ist (leeres Set). Klick darauf leert die Auswahl wieder (= alles wieder sichtbar).
 */
function erstelleAlleZeile(container, label, auswahlSet, onChange) {
	const zeile = document.createElement('div');
	zeile.className = `fopt alle${auswahlSet.size === 0 ? ' on' : ''}`;
	zeile.innerHTML = `<span class="cb"><i class="bi bi-check"></i></span><span class="fopt-label">${label}</span>`;
	zeile.addEventListener('click', () => {
		if (auswahlSet.size === 0) {
			return;
		}
		auswahlSet.clear();
		onChange();
	});
	container.appendChild(zeile);
}

/**
 * Baut die Filter-Panel UI (Bereich/Posten/Einsatzort/Qualifikation-Listen) neu auf.
 * @param {array<object>} bereiche unternehmen.Bereiche, bereits auf aktive Bereiche mit Posten gefiltert
 * @param {array<object>} postenListe
 * @param {array<object>} einsatzorte
 * @param {array<object>} qualifikationen
 * @param {array<object>} mitarbeiter für die Mitarbeitenden-Anzahl je Filteroption (Badge in jeder Zeile)
 * @param {function} onChange wird nach jeder Checkbox-Änderung aufgerufen (löst den Re-Render des Dienstplans aus)
 * @param {function} [onBereichAuswahl] (bereichID, angehakt, auswahl) => void — nach dem An-/Abwählen eines
 *   Bereichs (Panel-Zeile oder Chip); `auswahl` sind die danach gewählten BereichIDs, zuletzt angehakter
 *   zuletzt. Z.B. damit der Wochenplan seine Schichtvorlagen auf den passenden Bereich umstellt.
 */
function renderFilterPanel(bereiche, postenListe, einsatzorte, qualifikationen, mitarbeiter, onChange, onBereichAuswahl = null) {
	sicherstellenFilterZustand(bereiche, postenListe, einsatzorte, qualifikationen);
	const meldeBereich = onBereichAuswahl
		? (id, angehakt) => onBereichAuswahl(id, angehakt, [...bereichAuswahl])
		: null;
	// Wie ein Klick auf den zuletzt angehakten Bereich (Reihenfolge der Auswahl bleibt beim Speichern erhalten)
	if (meldeBereich && bereichAbgleichOffen) {
		bereichAbgleichOffen = false;
		if (bereichAuswahl.size > 0) meldeBereich([...bereichAuswahl].at(-1), true);
	}

	const bereichListe = document.getElementById('filter-bereich-liste');
	const postenListeEl = document.getElementById('filter-posten-liste');
	const einsatzortListeEl = document.getElementById('filter-einsatzort-liste');
	const qualiListe = document.getElementById('filter-qualifikation-liste');
	if (!bereichListe || !postenListeEl || !einsatzortListeEl || !qualiListe) {
		return; // Panel (noch) nicht im DOM, z.B. vor dem ersten Render
	}

	// Zeichnet nur das Panel (Listen + Chips) neu, ohne den Dienstplan neu zu laden — für sofortiges
	// visuelles Feedback beim Klick, bevor der (asynchrone) onChange-Re-Render durch ist. Klicks auf
	// "Alle" bzw. auf ein einzelnes Element wirken sich gegenseitig auf den Haken-Status der anderen
	// Zeilen der gleichen Kategorie aus, daher reicht ein reines Toggle der geklickten Zeile nicht.
	function neuZeichnen() {
		bereichListe.innerHTML = '';
		postenListeEl.innerHTML = '';
		einsatzortListeEl.innerHTML = '';
		qualiListe.innerHTML = '';

		// Alle vier Kategorien zeigen die Anzahl der jeweils zugeordneten Mitarbeitenden als nackte Zahl
		// (nicht z.B. die Postenanzahl je Bereich — das wurde zuvor als Mitarbeiteranzahl missverstanden).
		erstelleAlleZeile(bereichListe, 'Alle Bereiche', bereichAuswahl, aendern);
		bereiche.forEach((b) => {
			const postenIDsInBereich = postenListe.filter((p) => p.BereichID === b._id).map((p) => p._id);
			const anzahlMitarbeiter = mitarbeiter.filter((m) => m.Beschaeftigung[0].Posten.some((p) => postenIDsInBereich.includes(p.PostenID))).length;
			erstelleFilterZeile(bereichListe, b._id, b.Bereich, bereichAuswahl, anzahlMitarbeiter, aendern, meldeBereich);
		});

		erstelleAlleZeile(postenListeEl, 'Alle Posten', postenAuswahl, aendern);
		postenListe.forEach((p) => {
			const anzahlMitarbeiter = mitarbeiter.filter((m) => m.Beschaeftigung[0].Posten.some((mp) => mp.PostenID === p._id)).length;
			erstelleFilterZeile(postenListeEl, p._id, p.Posten, postenAuswahl, anzahlMitarbeiter, aendern);
		});

		erstelleAlleZeile(einsatzortListeEl, 'Alle Einsatzorte', einsatzortAuswahl, aendern);
		einsatzorte.forEach((e) => {
			const anzahlMitarbeiter = mitarbeiter.filter((m) => (m.Beschaeftigung[0].Einsatzorte ?? []).some((me) => me.EinsatzortID === e._id)).length;
			erstelleFilterZeile(einsatzortListeEl, e._id, e.Bezeichnung, einsatzortAuswahl, anzahlMitarbeiter, aendern);
		});

		erstelleAlleZeile(qualiListe, 'Alle Qualifikationen', qualifikationAuswahl, aendern);
		qualifikationen.forEach((q) => {
			const anzahlMitarbeiter = mitarbeiter.filter((m) => m.Beschaeftigung[0].Verguetung.some((v) => v.Verwenden && v.QualifikationID === q._id)).length;
			erstelleFilterZeile(qualiListe, q._id, q.BezeichnungNeutral, qualifikationAuswahl, anzahlMitarbeiter, aendern);
		});

		renderFilterChips(bereiche, postenListe, einsatzorte, qualifikationen, aendern, meldeBereich);
	}

	function aendern() {
		speichereFilterZustand();
		neuZeichnen();
		onChange();
	}

	aktuellesAendern = aendern;
	initSuchfeld();
	synchronisiereSuchfeld();
	neuZeichnen();
}

/**
 * Baut eine einzelne, entfernbare Filter-Chip-Pille.
 * @param {function} [onUmschalten] (id, angehakt=false) => void — nach dem Entfernen
 */
function erstelleChip(id, label, kategorieLabel, auswahlSet, bar, onChange, onUmschalten = null) {
	const chip = document.createElement('span');
	chip.className = 'fchip';
	chip.innerHTML = `<span class="k">${kategorieLabel}</span>${label}<span class="x"><i class="bi bi-x"></i></span>`;
	chip.querySelector('.x').addEventListener('click', () => {
		auswahlSet.delete(id);
		if (onUmschalten) onUmschalten(id, false);
		onChange();
	});
	bar.appendChild(chip);
}

/**
 * Zeigt die aktuell aktiven Filter (Bereich/Posten/Einsatzort/Qualifikation) als entfernbare Chips in
 * der .filterbar an — spiegelt exakt die `.fopt.on`-Auswahl des Filter-Panels (ohne die "Alle"-Zeilen).
 * Die Leiste bleibt versteckt, solange keine Kategorie eingeschränkt ist (Default: alles sichtbar).
 * @param {array<object>} bereiche
 * @param {array<object>} postenListe
 * @param {array<object>} einsatzorte
 * @param {array<object>} qualifikationen
 * @param {function} onChange
 * @param {function} [onBereichUmschalten] (bereichID, angehakt=false) => void — nach dem Entfernen eines Bereich-Chips
 */
function renderFilterChips(bereiche, postenListe, einsatzorte, qualifikationen, onChange, onBereichUmschalten = null) {
	const bar = document.getElementById('filterbar');
	if (!bar) {
		return;
	}
	const istGefiltert = bereichAuswahl.size > 0 || postenAuswahl.size > 0 || einsatzortAuswahl.size > 0 || qualifikationAuswahl.size > 0 || !!suchbegriff;
	bar.innerHTML = '';
	if (!istGefiltert) {
		bar.hidden = true;
		return;
	}
	bar.hidden = false;

	const label = document.createElement('span');
	label.className = 'flab';
	label.innerHTML = '<i class="bi bi-funnel-fill"></i>Filter';
	bar.appendChild(label);

	// Such-Chip: der Begriff ist Nutzereingabe, daher per textContent statt über erstelleChip (innerHTML).
	if (suchbegriff) {
		const chip = document.createElement('span');
		chip.className = 'fchip';
		chip.innerHTML = '<span class="k">Suche</span><span class="v"></span><span class="x"><i class="bi bi-x"></i></span>';
		chip.querySelector('.v').textContent = suchbegriff;
		chip.querySelector('.x').addEventListener('click', () => {
			suchbegriff = '';
			synchronisiereSuchfeld();
			onChange();
		});
		bar.appendChild(chip);
	}

	bereiche.forEach((b) => {
		if (bereichAuswahl.has(b._id)) erstelleChip(b._id, b.Bereich, 'Bereich', bereichAuswahl, bar, onChange, onBereichUmschalten);
	});
	postenListe.forEach((p) => {
		if (postenAuswahl.has(p._id)) erstelleChip(p._id, p.Posten, 'Posten', postenAuswahl, bar, onChange);
	});
	einsatzorte.forEach((e) => {
		if (einsatzortAuswahl.has(e._id)) erstelleChip(e._id, e.Bezeichnung, 'Einsatzort', einsatzortAuswahl, bar, onChange);
	});
	qualifikationen.forEach((q) => {
		if (qualifikationAuswahl.has(q._id)) erstelleChip(q._id, q.BezeichnungNeutral, 'Qualifikation', qualifikationAuswahl, bar, onChange);
	});

	const addChip = document.createElement('span');
	addChip.className = 'fchip add';
	addChip.innerHTML = '<i class="bi bi-plus-lg"></i>Filter hinzufügen';
	addChip.addEventListener('click', () => toggleFilterPanel());
	bar.appendChild(addChip);

	const sp = document.createElement('span');
	sp.className = 'sp';
	bar.appendChild(sp);

	const sichtbareBereiche = bereichAuswahl.size === 0 ? bereiche.length : bereichAuswahl.size;
	const summe = document.createElement('span');
	summe.className = 'fsum';
	summe.innerHTML = `<i class="bi bi-eye"></i>${sichtbareBereiche} von ${bereiche.length} Bereichen sichtbar`;
	bar.appendChild(summe);
}

/**
 * Setzt alle vier Filter auf "kein Filter" (= alles sichtbar) zurück und rendert das Panel + den
 * Dienstplan neu.
 */
function zuruecksetzenFilter(bereiche, postenListe, einsatzorte, qualifikationen, mitarbeiter, onChange) {
	resetFilterZustand();
	speichereFilterZustand();
	renderFilterPanel(bereiche, postenListe, einsatzorte, qualifikationen, mitarbeiter, onChange);
	onChange();
}

function toggleFilterPanel() {
	document.getElementById('filter-panel')?.classList.toggle('offen');
}

/**
 * Klappt eine einzelne Filter-Sektion (Bereich/Posten/Einsatzort/Qualifikation) ein/aus, analog zu
 * fun.togglePosten im Grid — rein visuell, keine Auswirkung auf den Filterzustand. Sitzt bewusst auf
 * dem statischen .fsh-Element in Dienstplaner.html statt in neuZeichnen(), damit der Ein-/Ausklapp-
 * Zustand über Filter-Änderungen (die die Liste selbst neu bauen) hinweg erhalten bleibt.
 * @param {HTMLElement} headerElement die geklickte .fsh-Zeile
 */
function toggleFilterSektion(headerElement) {
	headerElement.classList.toggle('eingeklappt');
	headerElement.nextElementSibling.classList.toggle('hidden');
}

export {
	istBereichSichtbar,
	istPostenSichtbar,
	istEinsatzortSichtbar,
	istQualifikationSichtbar,
	istSucheTreffer,
	istSucheAktiv,
	istSchichtTreffer,
	renderFilterPanel,
	zuruecksetzenFilter,
	toggleFilterPanel,
	toggleFilterSektion,
};
